import { z } from 'zod';
import {
  commentMentionEntityTypeSchema,
  objectiveHealthStatusSchema,
  workIntegrationReauthStageSchema,
  workIntegrationScheduleDisabledReasonSchema,
} from './notification-dto';

export const baseEmailDTOSchema = z.object({
  toAddresses: z.array(z.string()).min(1),
  subject: z.string(),
});

export const sendEmailDTOSchema = z.discriminatedUnion('template', [
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('organization_invite'),
    data: z.object({
      actionUrl: z.url(),
      organizationDisplayName: z.string().min(1),
      inviterName: z.string().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('contract_onboarding'),
    data: z.object({
      actionUrl: z.url(),
      companyName: z.string().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('multi_org_login'),
    data: z.object({
      organizations: z
        .array(
          z.object({
            displayName: z.string().min(1),
            url: z.url(),
          }),
        )
        .min(1),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('fim_magic_link'),
    data: z.object({
      verificationCode: z.string(),
      expiry: z.string(),
      logoUrl: z.url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('fim_invalid_email'),
    data: z.object({
      feedbackFormLink: z.url(),
      logoUrl: z.url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('welcome_trial'),
    data: z.object({
      name: z.string().min(1),
      organizationDisplayName: z.string().min(1),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('trialExpirationWarning'),
    data: z.object({
      name: z.string().min(1),
      organizationDisplayName: z.string().min(1),
      daysRemaining: z.number().positive(),
      locale: z.string().optional(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('trialExpired'),
    data: z.object({
      name: z.string().min(1),
      organizationDisplayName: z.string().min(1),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('invite_accepted'),
    data: z.object({
      acceptedByName: z.string().min(1),
      acceptedByEmail: z.string().min(1),
      organizationDisplayName: z.string().min(1),
      locale: z.string().optional(),
      actionUrl: z.url(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('feedback_submitted'),
    data: z.object({
      feedbackSummary: z.string(),
      feedbackDescription: z.string().optional(),
      feedbackFormName: z.string(),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      recipientName: z.string().optional(),
      notificationSettingsUrl: z.string().url().optional(),
      fields: z
        .array(
          z.object({
            name: z.string(),
            values: z.array(z.string()),
          }),
        )
        .optional(),
      attachments: z.array(z.string()).optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('idea_comment_notification'),
    data: z.object({
      commentText: z.string(),
      ideaTitle: z.string(),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      recipientName: z.string().optional(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('comment_mention_notification'),
    data: z.object({
      entityType: commentMentionEntityTypeSchema.optional(),
      entityTitle: z.string(),
      commentText: z.string(),
      commenterName: z.string().optional(),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      recipientName: z.string().optional(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('feedback_confirmation'),
    data: z
      .object({
        feedbackSummary: z.string().optional(),
        feedbackDescription: z.string().optional(),
        feedbackFormName: z.string(),
        contactName: z.string().optional(),
        viewFeedbackUrl: z.string().url(),
        portalLogoUrl: z.string().url().optional(),
        portalLogoData: z.string().optional(),
        portalLogoContentType: z.string().optional(),
        primaryColor: z.string().optional(),
        secondaryColor: z.string().optional(),
        unsubscribeUrl: z.string().url().optional(),
        fields: z
          .array(
            z.object({
              name: z.string(),
              values: z.array(z.string()),
            }),
          )
          .optional(),
        attachments: z.array(z.string()).optional(),
      })
      .refine(
        (data) => {
          const hasLogoData = data.portalLogoData !== undefined;
          const hasContentType = data.portalLogoContentType !== undefined;
          return hasLogoData === hasContentType;
        },
        {
          message: 'portalLogoData and portalLogoContentType must both be present or both absent',
        },
      ),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('stale_ideas_notification'),
    data: z.object({
      staleIdeasCount: z.number().positive(),
      ideaSamples: z.array(
        z.object({
          id: z.number(),
          title: z.string(),
        }),
      ),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      recipientName: z.string().optional(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('work_integration_reauth_notification'),
    data: z.object({
      name: z.string().min(1),
      stage: workIntegrationReauthStageSchema,
      sourceSystemType: z.string().optional(),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('work_integration_schedule_disabled_notification'),
    data: z.object({
      name: z.string().min(1),
      sourceSystemType: z.string().min(1),
      integrationName: z.string().min(1),
      reasonCode: workIntegrationScheduleDisabledReasonSchema,
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('work_integration_disconnected_notification'),
    data: z.object({
      name: z.string().min(1),
      integrationName: z.string().min(1),
      portfolioName: z.string().min(1),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('capacity_orphaned_instance_notification'),
    data: z.object({
      name: z.string().min(1),
      instanceName: z.string().nullish(),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('idea_completion_notification'),
    data: z.object({
      body: z.string(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('role_changed_notification'),
    data: z.object({
      recipientName: z.string().min(1),
      organizationDisplayName: z.string().min(1),
      oldRole: z.string().min(1),
      newRole: z.string().min(1),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('demand_framework_changed_notification'),
    data: z.object({
      name: z.string().min(1),
      previousFrameworkName: z.string().min(1),
      newFrameworkName: z.string().min(1),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('plan_published_notification'),
    data: z.object({
      name: z.string().min(1),
      publisherName: z.string().nullish(),
      portfolioName: z.string().min(1),
      locale: z.string().optional(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('objective_drift_detected_notification'),
    data: z.object({
      name: z.string().min(1),
      objectiveName: z.string(),
      previousStatus: objectiveHealthStatusSchema,
      newStatus: objectiveHealthStatusSchema,
      portfolioName: z.string().nullish(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
      locale: z.string().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('new_critical_insight_notification'),
    data: z.object({
      name: z.string().min(1),
      insightTitle: z.string(),
      portfolioName: z.string().nullish(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
      locale: z.string().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('new_high_insight_notification'),
    data: z.object({
      name: z.string().min(1),
      insightTitle: z.string(),
      portfolioName: z.string().nullish(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
      locale: z.string().optional(),
    }),
  }),
  z.object({
    ...baseEmailDTOSchema.shape,
    template: z.literal('credit_pool_low_notification'),
    data: z.object({
      name: z.string().min(1),
      organizationDisplayName: z.string(),
      actionUrl: z.string().url(),
      notificationSettingsUrl: z.string().url().optional(),
      locale: z.string().optional(),
    }),
  }),
]);

// Backward compatibility: email messages enqueued before the jira_reauth →
// work_integration_reauth rename still carry the legacy template name. Rewrite it to the
// current template before validation so in-flight emails still render during the
// transition window. Remove once the send-email queue has drained.
export const LEGACY_EMAIL_TEMPLATE_ALIASES: Record<string, string> = {
  jira_reauth_notification: 'work_integration_reauth_notification',
};

export const normalizeEmailTemplate = (template: string): string =>
  LEGACY_EMAIL_TEMPLATE_ALIASES[template] ?? template;
